import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { TailSpin } from "react-loader-spinner";
import { FaBus, FaSyncAlt, FaUtensils, FaMapMarkerAlt, FaChevronRight, FaExclamationCircle } from "react-icons/fa";
import FoodCard from "../components/FoodCard";
import { StoreContext } from "../context/StoreContext";

// Restaurant whose menu is shown as "on the way" dishes on this page.
const TRACK_BUS_RESTAURANT_ID = "R260427T104804M2741447085";

// Tracker id for the bus's OwnTracks device, used as the last path segment
// of the "latest location" endpoint.
const TRACKER_ID = "p2";
const LOCATION_ENDPOINT = `https://tavahot-api-stage.tarkshala.com/nexus/owntrack/location/latest/${TRACKER_ID}`;
const POLL_INTERVAL_MS = 60000;

// Zoom the map holds while following the bus: close enough to read the names
// of nearby streets/places, but not so tight that context is lost.
const BUS_FOCUS_ZOOM = 12;

// Hardcoded route: Source -> Stop1 -> Stop2 -> ... -> Destination.
const ROUTE_STOPS = [
  { id: "source", name: "Borivali (E) - Axis Bank, National Park Bridge End", lat: 19.228644188271325, lng: 72.86361353229965 },
  { id: "stop-1", name: "Kandivali (E) - Samta Nagar Police Chowki, Below Sky Walk Bridge", lat: 19.19930677466364, lng: 72.86123965568456 },
  { id: "stop-2", name: "Malad (E) - Omkar Tower, Next to Shantaram Talao", lat: 19.184057579803518, lng: 72.85862539213753 },
  { id: "stop-3", name: "Goregaon (E) - Karmarkar Travels, Before Aarey Flyover Bridge", lat: 19.169237351168466, lng: 72.8591180312839 },
  { id: "stop-4", name: "Andheri (E) - Neeta Travels, Western Tower Building, Bisleri Compound", lat: 19.110922208356115, lng: 72.85440502963455 },
  { id: "stop-5", name: "Vileparle (E) - Flora Marvel Bridge End", lat: 19.092510831254668, lng: 72.85006722506957 },
  { id: "stop-6", name: "Santacruz (E) - M.A. Travels, Teacher Colony Bridge End", lat: 19.079728984161683, lng: 72.84714150129047 },
  { id: "stop-7", name: "Bandra (E) - Kalanagar, Best Bus Stop Near Sky Walk", lat: 19.05382957068489, lng: 72.84840025814228 },
  { id: "stop-8", name: "Sion (E) - Purple Travels, Manmandir", lat: 19.04538153760882, lng: 72.8647865215456 },
  { id: "stop-9", name: "Chembur (E) - Maitry Park, Near S.T. Stand", lat: 19.04891960477788, lng: 72.90628221715068 },
  { id: "stop-10", name: "Vashi West - Vashi Plaza, Sector 17 Bridge", lat: 19.06894421258813, lng: 72.99776429795344 },
  { id: "stop-11", name: "Nerul West - London Pilsner Factory", lat: 19.049001847383384, lng: 73.02351366864654 },
  { id: "stop-12", name: "CBD Belapur - Below Flyover Bridge, Opp. R.T.O. Office", lat: 19.023050018608956, lng: 73.04545867042694 },
  { id: "stop-13", name: "Kharghar - Hiranandani Complex, Three Star Hotel", lat: 19.03283668377743, lng: 73.06650599032793 },
  { id: "stop-14", name: "Kalamboli - Kamothe Bus Stop, Near K.L.E. College, On Highway", lat: 19.025748374080116, lng: 73.09606496246529 },
  // { id: "stop-16", name: "Wakad Shani Mandir", lat: 18.608673053100055, lng: 73.75106883311278 },
  // { id: "stop-17", name: "Hinjewadi Bridge (Bhujbal Chowk)", lat: 18.592212664634715, lng: 73.75722596937933 },
  // { id: "stop-18", name: "Chandni Chowk", lat: 18.507453519374746, lng: 73.78300268674006 },
  // { id: "stop-19", name: "Shivaji Nagar Sancheti Hoshpital", lat: 18.529521445128278, lng: 73.85303245240966 },
  // { id: "stop-20", name: "Pune Railway Station Sasun Hoshpital Out Gate", lat: 18.52443323719693, lng: 73.86979221941716 },
  { id: "destination", name: "Swargate S.T. Stain Opposite", lat: 18.50003161570922, lng: 73.85852471284379 },
];

// Special standalone stop: the Khalapur food court where riders collect their
// prebooked meals. Deliberately kept out of ROUTE_STOPS so it never gets
// swallowed into a cluster bubble — it's rendered as its own animated
// food-icon marker (see foodCourtIcon) that is always visible on the map.
const FOOD_COURT_STOP = {
  id: "stop-15",
  name: "Khalapur Food Court - Get Your Prebooked Meals Here",
  lat: 18.79339264304362,
  lng: 73.29605156220998,
};

// Every stop in the exact order the bus visits them (source → … → Kalamboli
// → food court → destination). Tracking is one-directional, so this ordering
// is what lets us work out the next upcoming stop from the bus's current
// position. The food court lives here too even though it's drawn as a special
// standalone marker.
const ORDERED_STOPS = [
  ...ROUTE_STOPS.slice(0, -1),
  FOOD_COURT_STOP,
  ROUTE_STOPS[ROUTE_STOPS.length - 1],
];

const ROUTE_PATH = ROUTE_STOPS.map((stop) => [stop.lat, stop.lng]);
const ROUTE_BOUNDS = L.latLngBounds(ROUTE_PATH);

// Between Kalamboli and Pune the road network forks into two options: the
// old Mumbai-Pune highway (NH48, via the Khandala ghat) and the Mumbai-Pune
// Expressway (Yashwantrao Chavan Expressway). Our bus always takes the
// expressway, but a routing engine can't be told "prefer this named road" —
// the only reliable way to pin the path is to force it through waypoints
// that sit ON the expressway. These aren't real bus stops, so they're kept
// out of ROUTE_STOPS (no marker/label) and only used to build the routing
// request below.
const EXPRESSWAY_WAYPOINTS = [
  { lat: 18.834084121313342, lng: 73.24915152054436 }, //
  { lat: 18.71018884768814, lng: 73.56896541166346 }, // 
];

const ROUTE_WAYPOINTS = [
  ...ROUTE_STOPS.slice(0, -1),
  ...EXPRESSWAY_WAYPOINTS,
  ROUTE_STOPS[ROUTE_STOPS.length - 1],
];

// Public OSRM demo server: free, no API key, but rate-limited and not meant
// for heavy production traffic. Snaps the hardcoded stops (plus the
// expressway waypoints above) to the actual road network so the polyline
// follows real roads instead of straight lines.
const OSRM_ROUTE_ENDPOINT = `https://router.project-osrm.org/route/v1/driving/${ROUTE_WAYPOINTS.map(
  (stop) => `${stop.lng},${stop.lat}`
).join(";")}?overview=full&geometries=geojson`;

// Longitude degrees shrink towards the poles; scale them by the route's
// average latitude so the local projection below treats lat/lng as a
// roughly uniform flat plane for distance comparisons.
const ROUTE_REF_LAT_RAD =
  (ROUTE_STOPS.reduce((sum, stop) => sum + stop.lat, 0) / ROUTE_STOPS.length) *
  (Math.PI / 180);
const LNG_SCALE = Math.cos(ROUTE_REF_LAT_RAD);

const toPlane = ([lat, lng]) => [lng * LNG_SCALE, lat];
const fromPlane = ([x, y]) => [y, x / LNG_SCALE];

// Foot of the perpendicular from p onto segment ab, clamped to the segment.
const closestPointOnSegment = (p, a, b) => {
  const abx = b[0] - a[0];
  const aby = b[1] - a[1];
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq === 0) return a;
  let t = ((p[0] - a[0]) * abx + (p[1] - a[1]) * aby) / abLenSq;
  t = Math.max(0, Math.min(1, t));
  return [a[0] + t * abx, a[1] + t * aby];
};

// Snaps a raw [lat, lng] GPS fix onto the nearest point of the given
// polyline, so the bus marker always sits on the drawn route.
const snapToPolyline = (latLng, path) => {
  if (!path || path.length < 2) return latLng;

  const p = toPlane(latLng);
  let closest = null;
  let closestDistSq = Infinity;

  for (let i = 0; i < path.length - 1; i += 1) {
    const a = toPlane(path[i]);
    const b = toPlane(path[i + 1]);
    const candidate = closestPointOnSegment(p, a, b);
    const dx = candidate[0] - p[0];
    const dy = candidate[1] - p[1];
    const distSq = dx * dx + dy * dy;
    if (distSq < closestDistSq) {
      closestDistSq = distSq;
      closest = candidate;
    }
  }

  return closest ? fromPlane(closest) : latLng;
};

// The landmark detail of a stop name: the part after the " - " separator
// (empty when there's none, e.g. the destination "Swargate S.T. Stain
// Opposite").
const stopDetail = (name) => {
  if (!name) return "";
  const idx = name.indexOf(" - ");
  return idx === -1 ? "" : name.slice(idx + 3).trim();
};

// Given the bus's current [lat, lng], works out the next stop it's heading
// towards on this one-directional route. It finds the leg between two
// consecutive ordered stops that the bus is closest to and returns that leg's
// end stop. With no location yet, the bus hasn't started, so the "next" stop
// is the source. Always returns a stop.
const getNextStop = (latLng, stops) => {
  if (!latLng) return stops[0];

  const p = toPlane(latLng);
  let bestEndIdx = stops.length - 1;
  let bestDistSq = Infinity;

  for (let i = 0; i < stops.length - 1; i += 1) {
    const a = toPlane([stops[i].lat, stops[i].lng]);
    const b = toPlane([stops[i + 1].lat, stops[i + 1].lng]);
    const candidate = closestPointOnSegment(p, a, b);
    const dx = candidate[0] - p[0];
    const dy = candidate[1] - p[1];
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestEndIdx = i + 1;
    }
  }

  return stops[bestEndIdx];
};

// Keeps only the leading segment of a stop name (the area, before the
// " - <landmark>" detail), falling back to the full name when there's no
// separator (e.g. the destination "Swargate S.T. Stain Opposite").
const shortStopName = (name) => {
  if (!name) return "";
  const idx = name.indexOf(" - ");
  return (idx === -1 ? name : name.slice(0, idx)).trim() || name;
};

// A stop marker: a dot pinned exactly on the stop's lat/lng, with an
// always-visible name pill to its right. The divIcon box stays the size of
// the dot (so iconAnchor keeps the dot on the point) while the pill overflows
// to the right — divIcon doesn't clip its contents.
const stopIcon = (label, emphasize) => {
  const dot = emphasize ? 11 : 9;
  const dotColor = emphasize ? "#FBC02D" : "#171725";
  return L.divIcon({
    html: `<div style="
      display:inline-flex;
      align-items:center;
      gap:5px;
      padding:3px 9px 3px 6px;
      background:#ffffff;
      border-radius:9999px;
      border:1px solid rgba(0,0,0,0.08);
      box-shadow:0 2px 6px rgba(0,0,0,0.25);
      white-space:nowrap;
    ">
      <span style="
        flex:0 0 auto;
        width:${dot}px;
        height:${dot}px;
        border-radius:9999px;
        background:${dotColor};
        box-shadow:0 0 0 2px #ffffff, 0 0 2px rgba(0,0,0,0.35);
      "></span>
      <span style="
        color:#171725;
        font-size:11px;
        font-weight:${emphasize ? 700 : 600};
        line-height:1.2;
        letter-spacing:0.1px;
      ">${label}</span>
    </div>`,
    className: "",
    // Size is nominal — the chip overflows this box (divIcon doesn't clip).
    // Anchor points at the dot's centre so the chip is pinned to the stop.
    iconSize: [0, 0],
    iconAnchor: [7 + dot / 2, 11],
  });
};

// The food court marker: an attention-grabbing, gently bouncing red badge with
// a utensils icon, plus an always-visible name pill. Uses Tailwind's global
// `animate-bounce`/`animate-ping` utilities (Leaflet injects this html into the
// document, so those classes apply). Only the badge animates; the label stays
// still so it's easy to read.
const foodCourtIcon = (label) =>
  L.divIcon({
    html: `<div style="display:flex;align-items:center;white-space:nowrap;">
      <div style="position:relative;width:38px;height:38px;flex:0 0 auto;">
        <div class="animate-ping" style="
          position:absolute;inset:0;
          border-radius:9999px;
          background:#E53935;
          opacity:0.35;
        "></div>
        <div class="animate-bounce" style="
          position:relative;
          width:38px;height:38px;
          border-radius:9999px;
          background:#E53935;
          border:2px solid white;
          box-shadow:0 2px 6px rgba(0,0,0,0.5);
          display:flex;align-items:center;justify-content:center;
        ">
          ${ReactDOMServer.renderToStaticMarkup(<FaUtensils color="#ffffff" size={18} />)}
        </div>
      </div>
      <span style="
        margin-left:6px;
        padding:2px 8px;
        border-radius:9999px;
        background:#E53935;
        color:#ffffff;
        font-size:11px;
        font-weight:700;
        line-height:1.3;
        box-shadow:0 0 2px rgba(0,0,0,0.3);
      ">${label}</span>
    </div>`,
    className: "",
    iconSize: [38, 38],
    iconAnchor: [19, 19],
  });

const busIcon = (headingDeg) =>
  L.divIcon({
    html: `<div style="
      width:34px;
      height:34px;
      border-radius:9999px;
      background:#FBC02D;
      border:2px solid white;
      box-shadow:0 1px 4px rgba(0,0,0,0.5);
      display:flex;
      align-items:center;
      justify-content:center;
      transform:rotate(${Number.isFinite(headingDeg) ? headingDeg : 0}deg);
    ">
      <div style="transform:rotate(${Number.isFinite(headingDeg) ? -headingDeg : 0}deg);display:flex;">
        ${ReactDOMServer.renderToStaticMarkup(<FaBus color="#171725" size={16} />)}
      </div>
    </div>`,
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });

// Bubble shown in place of a group of nearby stops when the map is zoomed
// out. Styled to match the app (yellow fill, dark count) so it reads as part
// of the route rather than a default Leaflet cluster.
const createClusterIcon = (cluster) =>
  L.divIcon({
    html: `<div style="
      width:34px;
      height:34px;
      border-radius:9999px;
      background:#FBC02D;
      border:2px solid white;
      box-shadow:0 1px 4px rgba(0,0,0,0.5);
      display:flex;
      align-items:center;
      justify-content:center;
      color:#171725;
      font-size:13px;
      font-weight:700;
    ">${cluster.getChildCount()}</div>`,
    className: "",
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });

// Controls the map camera. Until the bus location is known it fits the whole
// route (initially the straight-line stops, then the road-following path once
// it loads) so the rider sees where the bus is headed. Once a bus position
// arrives it follows the bus: recentering it in the middle of the view at a
// readable zoom on every update.
const MapController = ({ routeBounds, busPosition }) => {
  const map = useMap();

  useEffect(() => {
    if (busPosition) return;
    map.fitBounds(routeBounds, { padding: [32, 32] });
  }, [map, routeBounds, busPosition]);

  useEffect(() => {
    if (!busPosition) return;
    map.setView(busPosition, BUS_FOCUS_ZOOM, { animate: true });
  }, [busPosition, map]);

  return null;
};

const TrackBus = () => {
  const navigate = useNavigate();
  const { setUseDefaultTimeToArrive } = useContext(StoreContext);
  const [busLocation, setBusLocation] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [roadPath, setRoadPath] = useState(null);
  const pollTimeoutRef = useRef(null);
  const refreshErrorTimeoutRef = useRef(null);

  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [defaultPriciestVariant, setDefaultPriciestVariant] = useState(false);
  const [showMorePopular, setShowMorePopular] = useState(false);

  // The bus's own arrival decides when the customer reaches the restaurant —
  // checkout must skip the "when will you arrive" ETA prompt for these users.
  useEffect(() => {
    setUseDefaultTimeToArrive(true);
  }, [setUseDefaultTimeToArrive]);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const requestData = {
          type: "COMPLETE",
          restaurantId: TRACK_BUS_RESTAURANT_ID,
          filters: [
            {
              type: "AVAILABILITY",
              value: true,
            },
          ],
        };

        const response = await axios.post(
          "/api/catalogue/restaurant/menu/v2",
          requestData,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const responseData = response?.data?.data || {};
        setMenuItems(responseData.items || []);
        setDefaultPriciestVariant(Boolean(responseData.defaultPriciestVariant));
      } catch (e) {
        console.error("Failed to fetch dishes on the way:", e);
      } finally {
        setMenuLoading(false);
      }
    };

    fetchMenuItems();
  }, []);

  const frequentDishes = useMemo(
    () =>
      menuItems.filter((item) =>
        showMorePopular ? item.frequentRating === 1 || item.frequentRating === 2 : item.frequentRating === 2
      ),
    [menuItems, showMorePopular]
  );

  const handleShowFullMenu = () => {
    navigate(`/menu-items/${TRACK_BUS_RESTAURANT_ID}`, {
      state: {
        restaurantId: TRACK_BUS_RESTAURANT_ID,
      },
    });
  };

  // One-off fetch: the stops are hardcoded, so the road-following geometry
  // between them never changes and doesn't need to be refetched on poll.
  useEffect(() => {
    let cancelled = false;

    const fetchRoadRoute = async () => {
      try {
        const response = await axios.get(OSRM_ROUTE_ENDPOINT);
        const coordinates = response?.data?.routes?.[0]?.geometry?.coordinates;
        if (!cancelled && Array.isArray(coordinates) && coordinates.length > 1) {
          setRoadPath(coordinates.map(([lng, lat]) => [lat, lng]));
        }
      } catch (e) {
        console.error("Failed to fetch road-following route, falling back to straight lines:", e);
      }
    };

    fetchRoadRoute();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchBusLocation = useCallback(async () => {
    try {
      const response = await axios.get(LOCATION_ENDPOINT, {
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const data = response?.data?.data;
      const message = data?.message;

      if (!response?.data?.success || !message) {
        throw new Error("Bus location unavailable right now.");
      }

      setBusLocation({
        lat: message.lat,
        lng: message.lng ?? message.lon,
        heading: message.cog,
      });
      setLastUpdatedAt(data.createdAt);
      return true;
    } catch (e) {
      console.error("Failed to fetch bus location:", e);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Manual refresh: fetch the latest available bus fix on demand and let the
  // map recenter on it. Background polling keeps the last known location on
  // screen, so a failed refresh just tells the user to retry — it never wipes
  // the map or nags with a persistent error banner.
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(false);
    clearTimeout(refreshErrorTimeoutRef.current);

    const ok = await fetchBusLocation();
    setRefreshing(false);

    if (!ok) {
      // Show a quiet, self-dismissing error right below the button instead of
      // a popup — it fades away on its own after a few seconds.
      setRefreshError(true);
      refreshErrorTimeoutRef.current = setTimeout(
        () => setRefreshError(false),
        4000
      );
    }
  }, [fetchBusLocation]);

  useEffect(
    () => () => clearTimeout(refreshErrorTimeoutRef.current),
    []
  );

  useEffect(() => {
    fetchBusLocation();

    const poll = () => {
      pollTimeoutRef.current = setTimeout(async () => {
        await fetchBusLocation();
        poll();
      }, POLL_INTERVAL_MS);
    };
    poll();

    return () => clearTimeout(pollTimeoutRef.current);
  }, [fetchBusLocation]);

  const busMarkerIcon = useMemo(
    () => busIcon(busLocation?.heading),
    [busLocation?.heading]
  );

  const foodCourtMarkerIcon = useMemo(
    () => foodCourtIcon(shortStopName(FOOD_COURT_STOP.name)),
    []
  );

  const displayPath = roadPath || ROUTE_PATH;
  const displayBounds = useMemo(
    () => (roadPath ? L.latLngBounds(roadPath) : ROUTE_BOUNDS),
    [roadPath]
  );

  // Raw GPS fixes rarely land exactly on the road; snap the marker to the
  // nearest point on the drawn polyline (foot of the perpendicular) so it
  // always renders on the line.
  const snappedBusPosition = useMemo(() => {
    if (!busLocation) return null;
    return snapToPolyline([busLocation.lat, busLocation.lng], displayPath);
  }, [busLocation, displayPath]);

  // One-directional route: the next stop the bus is heading for, derived from
  // its current position. Before any location arrives, this is the source.
  const nextStop = useMemo(
    () =>
      getNextStop(
        busLocation ? [busLocation.lat, busLocation.lng] : null,
        ORDERED_STOPS
      ),
    [busLocation]
  );
  const hasBusLocation = Boolean(busLocation);
  const nextStopDetail = stopDetail(nextStop.name);

  const lastUpdatedLabel = lastUpdatedAt
    ? new Date(lastUpdatedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen pt-24 px-4">
        <div className="text-center">
          <TailSpin height="80" width="80" color="#FBC02D" ariaLabel="loading" radius="1" visible />
          <p className="text-gray-600 mt-4">Locating the bus...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen pt-24 px-4 pb-8 flex flex-col">
      <div className="max-w-md w-full mx-auto flex flex-col">
        <h1 className="text-2xl font-bold text-[#171725] mb-1">Track Bus</h1>
        <p className="text-gray-600 mb-4">
          Live position of the bus along its route.
        </p>

        <div className="mb-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              {lastUpdatedLabel
                ? `Bus location as of ${lastUpdatedLabel}`
                : "Bus location not available yet"}
            </p>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-1.5 rounded-full bg-[#FBC02D] px-3 py-1.5 text-xs font-semibold text-[#171725] shadow-sm transition hover:brightness-95 active:scale-95 disabled:opacity-60"
            >
              <FaSyncAlt size={11} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing..." : "Refresh location"}
            </button>
          </div>

          {refreshError && (
            <div className="mt-1.5 flex items-center justify-end gap-1 text-xs text-red-500 animate-pulse">
              <FaExclamationCircle size={12} className="flex-none" />
              <span>Couldn't refresh. Please try again.</span>
            </div>
          )}
        </div>

        <div className="relative z-0 rounded-2xl overflow-hidden shadow w-full aspect-square">
          <MapContainer
            center={[ROUTE_STOPS[0].lat, ROUTE_STOPS[0].lng]}
            zoom={12}
            scrollWheelZoom
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            <Polyline positions={displayPath} pathOptions={{ color: "#1E88E5", weight: 4 }} />

            <MarkerClusterGroup
              iconCreateFunction={createClusterIcon}
              maxClusterRadius={50}
              showCoverageOnHover={false}
              spiderfyOnMaxZoom
            >
              {ROUTE_STOPS.map((stop, index) => {
                const isEndpoint =
                  index === 0 || index === ROUTE_STOPS.length - 1;
                return (
                  <Marker
                    key={stop.id}
                    position={[stop.lat, stop.lng]}
                    icon={stopIcon(shortStopName(stop.name), isEndpoint)}
                  />
                );
              })}
            </MarkerClusterGroup>

            {/* Food court: standalone marker outside the cluster group so it
                is always visible and never merges into a cluster bubble. */}
            <Marker
              position={[FOOD_COURT_STOP.lat, FOOD_COURT_STOP.lng]}
              icon={foodCourtMarkerIcon}
              zIndexOffset={500}
            />

            {snappedBusPosition && (
              <Marker
                position={snappedBusPosition}
                icon={busMarkerIcon}
                zIndexOffset={1000}
              />
            )}

            <MapController
              routeBounds={displayBounds}
              busPosition={snappedBusPosition}
            />
          </MapContainer>

          {/* Loading overlay while a manual refresh is in flight — stays until
              the fetch completes or fails. */}
          {refreshing && (
            <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/50 backdrop-blur-[1px]">
              <TailSpin
                height="56"
                width="56"
                color="#FBC02D"
                ariaLabel="refreshing-location"
                radius="1"
                visible
              />
            </div>
          )}
        </div>

        {/* Next upcoming stop on this one-directional route. Material-style
            card: leading icon, overline label, primary + secondary text. */}
        <div className="mt-4 flex items-center gap-3 rounded-2xl bg-white p-4 shadow-md ring-1 ring-black/5">
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[#FBC02D]/20">
            <FaMapMarkerAlt size={18} className="text-[#E53935]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              {hasBusLocation ? "Next stop" : "Starting from"}
            </p>
            <p className="truncate text-sm font-semibold text-[#171725]">
              {shortStopName(nextStop.name)}
            </p>
            {nextStopDetail && (
              <p className="truncate text-xs text-gray-500">{nextStopDetail}</p>
            )}
          </div>
          <FaChevronRight size={14} className="flex-none text-gray-300" />
        </div>

        {!menuLoading && frequentDishes.length > 0 && (
          <div className="mt-6">
            <h2 className="text-xl font-bold text-[#171725] mb-3">
              Grab Free Tea <span className="text-[#FBC02D]">by preordering</span>
            </h2>
            <div className="rounded-2xl border border-[#FBC02D] bg-white px-4">
              <div className="space-y-2 py-2">
                {frequentDishes.map((item) => (
                  <FoodCard
                    key={item.dishId}
                    name={item.name}
                    price={item.price}
                    rating={item.rating}
                    ratingCounts={item.ratingCounts}
                    thumbnailUrl={item.thumbnailUrl}
                    dishId={item.dishId}
                    restaurantId={TRACK_BUS_RESTAURANT_ID}
                    availableNow={item.availableNow}
                    variations={item.variations}
                    addOnGroups={item.addOnGroups}
                    description={item.description || item.shortDescription || item.itemDescription || ""}
                    foodPreference={item.foodPreference}
                    defaultPriciestVariant={defaultPriciestVariant}
                  />
                ))}
              </div>
            </div>

            <button
              onClick={showMorePopular ? handleShowFullMenu : () => setShowMorePopular(true)}
              className="w-full mt-4 bg-[#171725] text-white font-semibold py-3 rounded-lg hover:bg-black transition-colors"
            >
              {showMorePopular ? "Show full menu" : "Show more popular items"}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default TrackBus;
